// ============================================================================
// Month-end queue — the landing screen for the Incentive module (CW-N P2).
//
// Top:    two live trackers for the OPEN month + OPEN quarter (live-tracker.tsx).
//         Nothing is paid from those; they exist so an AM can see where a sub
//         stands before the period closes.
// Below:  the closed periods that actually pay, monthly on the left and
//         quarterly on the right, most recent first. The most recent closed
//         period is the one that pays this cycle, so it is rendered larger,
//         first, with its action inline — no expanding, no scrolling past the
//         live cards to find it.
//
// No period picker anywhere: the screen opens on the current period plus
// whatever is still outstanding behind it.
// ============================================================================

import { useState } from 'react';
import { ErrorState } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import {
  useIncentiveQueue,
  useIncentiveLiveTracker,
  generateIncentivePayouts,
  type PeriodNeedingReview,
  type QueueStatus,
} from './hooks/use-incentive-data';
import type { PeriodSelection } from './index';
import { formatPeriod, payoutTypeShort } from './format';
import { LiveTrackers } from './live-tracker';

const QUEUE_BADGE: Record<QueueStatus, { label: string; cls: string }> = {
  not_generated: {
    label: 'Not generated',
    cls: 'bg-st-warn-bg text-st-warn-fg',
  },
  needs_validation: {
    label: 'Needs validation',
    cls: 'bg-st-warn-bg text-st-warn-fg',
  },
  // Step 1 done for every row; waiting on the approver (CW-N, 2026-08-13).
  needs_approval: {
    label: 'Needs approval',
    cls: 'bg-st-info-bg text-st-info-fg',
  },
  ready_for_finance: {
    label: 'Ready for finance',
    cls: 'bg-st-info-bg-strong text-st-info-fg',
  },
  complete: { label: 'Complete', cls: 'bg-rag-green/15 text-rag-green' },
};

const RECONSTRUCTED_BADGE = {
  label: 'Complete — reconstructed from AP',
  cls: 'bg-rag-green/15 text-rag-green',
};

// CW-Q 4.2. A period the app never ran, holding nothing, is not "not generated".
const PRE_APP_BADGE = {
  label: 'Before the app — no rows to review',
  cls: 'bg-tcs-bg-muted text-tcs-text-muted',
};

// ---------------------------------------------------------------------------
// Pre-app history (CW-N 2c). PRESENTATION ONLY — the view is not touched.
//
// expected_count is computed against LIVE config, so a period that predates
// the app looks under-generated for ever: April showed 12 of 31 expected, May
// 13 of 31, and both would render "Not generated" despite being fully paid
// out of tagged AP sub-payment cycles on 2026-08-13.
//
// A period is treated as reconstructed when it would otherwise be mislabelled
// "Not generated" (generated < expected) yet has rows, none of them still in
// flight, and some of them paid. The `queue_status === 'not_generated'` guard
// matters: without it a genuinely finished period would also match these
// counts and get told it came from AP, which is not true.
export function isReconstructed(r: PeriodNeedingReview): boolean {
  return (
    r.queue_status === 'not_generated' &&
    r.generated_count > 0 &&
    r.pending_review_count === 0 &&
    r.am_validated_count === 0 &&
    r.verified_count === 0 &&
    r.paid_count > 0
  );
}

// ---------------------------------------------------------------------------
// A period the app never ran, holding nothing (CW-Q 4.2). PRESENTATION ONLY.
//
// June 2026 has ZERO payout rows. `isReconstructed` requires `paid_count > 0`,
// so June fell through to plain `not_generated`, rendered an orange
// "Not generated" badge, and offered a live **Generate** button to a full AM.
// Pressing it would have materialised payout rows — real money — for a month
// that predates the app: the generator derives eligibility from LIVE config, so
// it would have invented 31 payouts nobody ever earned or missed, against no
// evidence anyone could check.
//
// THE GO-LIVE BOUNDARY IS DERIVED FROM DATA, not written down. It is the
// earliest period the app's own generator produced — i.e. the earliest period
// with rows that is NOT a reconstruction. Measured 2026-08-14 from
// `v_incentive_periods_needing_review`:
//
//   2026-07  31 generated, 29 pending_review   -> app-generated  <- the boundary
//   2026-06   0 generated                      -> pre-app hole
//   2026-05  13 generated, 13 paid             -> reconstructed from AP
//   2026-04  12 generated, 12 paid             -> reconstructed from AP
//
// The query behind it: `SELECT period, generated_count, pending_review_count,
// paid_count FROM v_incentive_periods_needing_review WHERE payout_type =
// 'no_misses_monthly' ORDER BY period` — the same rows this component already
// holds, which is the point. It self-corrects: when August generates, the
// boundary moves on its own and no constant needs editing.
//
// Deliberately NOT expressed as "any period with zero rows". A CURRENT period
// with zero rows genuinely does need generating, and hiding its button would be
// the opposite bug.
export function firstAppGeneratedPeriod(
  rows: readonly PeriodNeedingReview[],
): string | null {
  const generated = rows
    .filter((r) => r.generated_count > 0 && !isReconstructed(r))
    .map((r) => r.period)
    .sort();
  return generated[0] ?? null;
}

export function isPreAppEmpty(
  r: PeriodNeedingReview,
  rows: readonly PeriodNeedingReview[],
): boolean {
  if (r.generated_count > 0) return false;
  const boundary = firstAppGeneratedPeriod(
    rows.filter((x) => x.payout_type === r.payout_type),
  );
  // No boundary yet means the app has generated nothing at all, so there is
  // nothing to be "before". Leave those periods alone.
  if (boundary === null) return false;
  return r.period < boundary;
}

export function QueueView({
  isFullAm,
  onOpenPeriod,
  refreshKey = 0,
}: {
  isFullAm: boolean;
  onOpenPeriod: (sel: PeriodSelection) => void;
  /** CW-N 3d — bumped by any mutation elsewhere in the module. */
  refreshKey?: number;
}) {
  const { rows, isLoading, error, refetch } = useIncentiveQueue(refreshKey);
  const tracker = useIncentiveLiveTracker(refreshKey);

  if (error) return <ErrorState body={error} onRetry={refetch} />;

  // Reverse chronological within each type.
  const byType = (t: PeriodNeedingReview['payout_type']) =>
    rows
      .filter((r) => r.payout_type === t)
      .slice()
      .sort((a, b) => b.period.localeCompare(a.period));

  const monthly = byType('no_misses_monthly');
  const quarterly = byType('compliment_quarterly');

  return (
    <div className="space-y-5">
      <LiveTrackers
        rows={tracker.rows}
        isLoading={tracker.isLoading}
        error={tracker.error}
      />

      <div>
        <h2 className="text-sm font-semibold text-tcs-text">
          Closed periods — these are what pay
        </h2>
        <p className="mt-0.5 text-xs text-tcs-text-muted">
          Most recent first. Closed accounts, closed facilities and ended
          assignments are excluded automatically — eligibility is derived live,
          there is no static list.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-tcs-text-muted">Loading queue…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-tcs-text-muted">
          No recent periods to review.
        </p>
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
          <PeriodColumn
            heading="Monthly · Scope Performance"
            rows={monthly}
            isFullAm={isFullAm}
            onOpenPeriod={onOpenPeriod}
            onGenerated={refetch}
          />
          <PeriodColumn
            heading="Quarterly · Compliment"
            rows={quarterly}
            isFullAm={isFullAm}
            onOpenPeriod={onOpenPeriod}
            onGenerated={refetch}
          />
        </div>
      )}
    </div>
  );
}

function PeriodColumn({
  heading,
  rows,
  isFullAm,
  onOpenPeriod,
  onGenerated,
}: {
  heading: string;
  rows: PeriodNeedingReview[];
  isFullAm: boolean;
  onOpenPeriod: (sel: PeriodSelection) => void;
  onGenerated: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
        {heading}
      </div>
      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-tcs-border bg-tcs-surface p-3 text-xs text-tcs-text-muted">
          No closed periods yet.
        </p>
      ) : (
        rows.map((r, i) => (
          <QueueCard
            key={`${r.payout_type}-${r.period}`}
            row={r}
            // The pre-app test needs the whole column, not just this row: the
            // boundary is derived from its siblings (CW-Q 4.2).
            preAppEmpty={isPreAppEmpty(r, rows)}
            // The first card in each column is the period that pays this
            // cycle. It gets the weight and the inline action.
            primary={i === 0}
            isFullAm={isFullAm}
            onOpen={() =>
              onOpenPeriod({ period: r.period, payoutType: r.payout_type })
            }
            onGenerated={onGenerated}
          />
        ))
      )}
    </div>
  );
}

// Exported so an acceptance pass can render one card in isolation and READ what
// it produces. "A green build is not evidence" — CW-Q 4.2 required observing
// June with no badge and no Generate button, and a component you cannot mount
// without a Supabase client cannot be observed.
export function QueueCard({
  row,
  primary,
  isFullAm,
  preAppEmpty,
  onOpen,
  onGenerated,
}: {
  row: PeriodNeedingReview;
  primary: boolean;
  isFullAm: boolean;
  /** CW-Q 4.2 — a period the app never ran, holding nothing. */
  preAppEmpty: boolean;
  onOpen: () => void;
  onGenerated: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const reconstructed = isReconstructed(row);
  const badge = reconstructed
    ? RECONSTRUCTED_BADGE
    : preAppEmpty
      ? PRE_APP_BADGE
      : QUEUE_BADGE[row.queue_status];
  // Generating a reconstructed period would materialise fresh rows against
  // history that is already paid — hide the button, not just disable it.
  // Same for a pre-app period holding nothing: generating June 2026 would mint
  // 31 payouts for a month nobody tracked (CW-Q 4.2).
  const canGenerate =
    isFullAm &&
    row.queue_status === 'not_generated' &&
    !reconstructed &&
    !preAppEmpty;

  const outstanding =
    row.pending_review_count + row.am_validated_count + row.verified_count;

  async function generate() {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const res = await generateIncentivePayouts(row.period, row.payout_type);
      setNote(
        `Generated ${res.created} row${res.created === 1 ? '' : 's'}` +
          (res.skipped ? ` (${res.skipped} already existed)` : ''),
      );
      onGenerated();
    } catch (e) {
      setError(errorMessage(e, 'Generation failed.', 'incentive'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={[
        'rounded-xl border bg-tcs-bg',
        primary
          ? 'border-tcs-border-strong p-4 shadow-sm'
          : 'border-tcs-border p-3',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div
            className={[
              'font-semibold text-tcs-text',
              primary ? 'text-base' : 'text-sm',
            ].join(' ')}
          >
            {formatPeriod(row.period)}
          </div>
          <div className="text-xs text-tcs-text-muted">
            {payoutTypeShort(row.payout_type)}
            {primary && outstanding > 0 && (
              <>
                {' · '}
                <span className="font-semibold text-tcs-text">
                  {outstanding}
                </span>{' '}
                outstanding
              </>
            )}
          </div>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.cls}`}
        >
          {badge.label}
        </span>
      </div>

      <dl className="mt-3 grid grid-cols-4 gap-1.5 text-center text-xs">
        <Stat label="Eligible" value={row.expected_count} />
        <Stat label="Generated" value={row.generated_count} />
        <Stat label="Pending" value={row.pending_review_count} />
        <Stat label="Validated" value={row.am_validated_count} />
        <Stat label="Approved" value={row.verified_count} />
        <Stat label="Disqualified" value={row.disqualified_count} />
        <Stat label="Paid" value={row.paid_count} />
        <Stat label="To finance" value={row.sent_to_finance_count} />
      </dl>

      {reconstructed && (
        <p className="mt-2 text-[11px] text-tcs-text-muted">
          Reconstructed from tagged AP sub-payment cycles on 2026-08-13; every
          row carries a note saying so. “Eligible” is computed against today’s
          config, which is why it reads higher than what was actually paid.
        </p>
      )}

      {preAppEmpty && (
        <p className="mt-2 text-[11px] text-tcs-text-muted">
          This month closed before the app created any payouts, and no rows exist
          for it. Generating it would create payments nobody earned or missed —
          “Eligible” is computed against today’s config, not against what was
          tracked at the time — so there is no Generate button here. If this month
          really does owe someone, it has to come from the AP record, the way
          April and May did.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {canGenerate && (
          <button
            type="button"
            onClick={generate}
            disabled={busy}
            className="rounded-md bg-tcs-inverse px-3 py-1.5 text-xs font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-60"
          >
            {busy ? 'Generating…' : 'Generate'}
          </button>
        )}
        <button
          type="button"
          onClick={onOpen}
          className={
            primary && !canGenerate
              ? 'rounded-md bg-tcs-inverse px-3 py-1.5 text-xs font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover'
              : 'rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs text-tcs-text hover:bg-tcs-surface'
          }
        >
          Open payouts
        </button>
      </div>

      {note && <p className="mt-2 text-xs text-rag-green">{note}</p>}
      {error && <p className="mt-2 text-xs text-st-danger-fg">{error}</p>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-tcs-bg-muted px-1.5 py-1.5">
      <div className="text-sm font-semibold tabular-nums text-tcs-text">
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
        {label}
      </div>
    </div>
  );
}
